import { useId, useState } from "react";
import { Button } from "~/components/ui/button";
import { DialogFooter } from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { MarkdownEditor } from "~/components/ui/markdown-editor";
import { Textarea } from "~/components/ui/textarea";
import {
  SKILL_CONTENT_MAX_LENGTH,
  SKILL_DESCRIPTION_MAX_LENGTH,
  SKILL_NAME_MAX_LENGTH,
  SKILL_NAME_MIN_LENGTH,
  SKILL_NAME_PATTERN,
} from "~/services/skills";

export interface SkillFormValues {
  name: string;
  description: string;
  content: string;
}

export type SkillSubmitResult = { ok: true } | { ok: false; field?: "name"; message?: string };

interface AgentSkillFormProps {
  initial: SkillFormValues;
  saveLabel: string;
  onSubmit: (values: SkillFormValues) => Promise<SkillSubmitResult>;
  onCancel: () => void;
  readOnly?: boolean;
}

const validateName = (name: string): string | null => {
  if (!name) {
    return "Name is required";
  }
  if (name.length < SKILL_NAME_MIN_LENGTH) {
    return `Name must be at least ${SKILL_NAME_MIN_LENGTH} characters`;
  }
  if (name.length > SKILL_NAME_MAX_LENGTH) {
    return `Name must be ${SKILL_NAME_MAX_LENGTH} characters or less`;
  }
  if (name.endsWith("-")) {
    return "Name cannot end with a hyphen";
  }
  if (!SKILL_NAME_PATTERN.test(name)) {
    return "Use lowercase letters, numbers, and hyphens, starting with a letter";
  }
  return null;
};

const validateDescription = (description: string): string | null => {
  if (!description.trim()) {
    return "Description is required";
  }
  if (description.length > SKILL_DESCRIPTION_MAX_LENGTH) {
    return `Description must be ${SKILL_DESCRIPTION_MAX_LENGTH} characters or less`;
  }
  return null;
};

const validateContent = (content: string): string | null => {
  if (!content.trim()) {
    return "Instructions are required";
  }
  if (content.length > SKILL_CONTENT_MAX_LENGTH) {
    return `Instructions must be ${SKILL_CONTENT_MAX_LENGTH.toLocaleString()} characters or less`;
  }
  return null;
};

export const AgentSkillForm = ({
  initial,
  saveLabel,
  onSubmit,
  onCancel,
  readOnly = false,
}: AgentSkillFormProps) => {
  const [form, setForm] = useState<SkillFormValues>(initial);
  const [touched, setTouched] = useState({ name: false, description: false, content: false });
  const [submitting, setSubmitting] = useState(false);
  const [nameServerError, setNameServerError] = useState<string | null>(null);

  const nameId = useId();
  const descriptionId = useId();
  const contentId = useId();

  const clientNameError = validateName(form.name);
  const nameError = nameServerError ?? clientNameError;
  const descriptionError = validateDescription(form.description);
  const contentError = validateContent(form.content);
  const isValid = !clientNameError && !descriptionError && !contentError;

  const showNameError = (touched.name && !!clientNameError) || !!nameServerError;
  const showDescriptionError = touched.description && !!descriptionError;
  const showContentError = touched.content && !!contentError;

  const handleSubmit = async () => {
    if (!isValid || submitting) {
      return;
    }
    setSubmitting(true);
    setNameServerError(null);
    try {
      const result = await onSubmit(form);
      if (!result.ok && result.field === "name") {
        setNameServerError(result.message ?? "Name is already in use on this agent");
        setTouched((t) => ({ ...t, name: true }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-6 pt-4 pb-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={nameId} className="gap-0">
            Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id={nameId}
            placeholder="my-skill-name"
            value={form.name}
            onChange={(e) => {
              setForm((f) => ({ ...f, name: e.target.value }));
              setNameServerError(null);
            }}
            onBlur={() => setTouched((t) => ({ ...t, name: true }))}
            aria-invalid={showNameError}
            disabled={submitting || readOnly}
            autoComplete="off"
            data-1p-ignore
          />
          {!readOnly && showNameError && (
            <span className="text-xs text-destructive">{nameError}</span>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor={descriptionId} className="gap-0">
            Description <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id={descriptionId}
            rows={3}
            placeholder="What this skill does and when to use it…"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            onBlur={() => setTouched((t) => ({ ...t, description: true }))}
            maxLength={SKILL_DESCRIPTION_MAX_LENGTH}
            aria-invalid={showDescriptionError}
            disabled={submitting || readOnly}
          />
          {!readOnly && (
            <div className="flex items-start justify-between gap-2">
              {showDescriptionError ? (
                <span className="text-xs text-destructive">{descriptionError}</span>
              ) : (
                <span />
              )}
              <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                {form.description.length} / {SKILL_DESCRIPTION_MAX_LENGTH}
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor={contentId} className="gap-0">
            Instructions <span className="text-destructive">*</span>
          </Label>
          <MarkdownEditor
            id={contentId}
            value={form.content}
            onChange={(content) => setForm((f) => ({ ...f, content }))}
            onBlur={() => setTouched((t) => ({ ...t, content: true }))}
            readOnly={readOnly || submitting}
            invalid={showContentError}
            maxLength={SKILL_CONTENT_MAX_LENGTH}
            paneClassName="h-[320px] overflow-y-auto"
          />
          {!readOnly && showContentError && (
            <span className="text-xs text-destructive">{contentError}</span>
          )}
        </div>
      </div>

      <DialogFooter className="shrink-0 border-t border-border px-6 py-3">
        {readOnly ? (
          <Button onClick={onCancel} size="sm">
            Close
          </Button>
        ) : (
          <>
            <Button variant="ghost" size="sm" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isValid || submitting}
              loading={submitting}
              size="sm"
            >
              {saveLabel}
            </Button>
          </>
        )}
      </DialogFooter>
    </>
  );
};
